// Third Party
import * as React from 'react';
import * as Sentry from '@sentry/nextjs';
import { GetServerSidePropsContext } from 'next';
import { v4 as uuid } from 'uuid';

// Utils
import { auth } from '@auth';
import {
  imperativeNodeFeaturesListAllForSearchFiltersQuery,
  imperativeNodeAccountBySlug,
} from '@utils/imperative-node-queries';
import { isSSR } from '@utils/predicates';
import { formatDateYYYYMMDD } from '@utils/date-helpers';
import { APOLLO_STATE_PROP_NAME } from '@utils/apollo-react-client';
import { createApolloClient } from '@utils/apollo-node-client';
import { useToggle, useDebounce } from '@hooks';
import {
  FeatureKey,
  useListingsSearchQuery,
  ListingsSearchType,
  ListingsSearchQuery,
  PropertyType,
  ListingsSearchDocument,
  UniquePropertyType,
} from '@gql';
import { useMobileSearchModal } from '@components/mobile-search-modal';
import { guestDescription } from '@utils/guest-description';
import { useAnalytics } from './use-analytics';
import { routes } from '@routes';

export type Feature = {
  key: FeatureKey;
  displayName: string;
  categoryName: string;
  promoted: boolean;
};

type Point = { lat: number; lng: number };

export type Geometry = {
  centerPoint: Point;
  northEastPoint: Point;
  southWestPoint: Point;
};

type SearchContext = {
  /** Activate the mobile feature modal */
  activateFeaturesModal: VoidFunction;
  /** Deactivate the mobile feature modal */
  deactivateFeaturesModal: VoidFunction;
  /** Activate the mobile search modal */
  activateMobileSearchModal: VoidFunction;
  /** Deactivate the mobile search modal */
  deactivateMobileSearchModal: VoidFunction;
  /** Activate the search nav form */
  activateSearchNavForm: VoidFunction;
  /** Deactivate the search nav form */
  deactivateSearchNavForm: VoidFunction;
  /** All of the filters currently applied to the search */
  activeFilters: Feature[];
  /** All of the available search features */
  allFeatures: Feature[];
  /** Region search results data */
  listingsQueryResults?: ListingsSearchQuery;
  /** Region search results data loading flag */
  listingsLoading: boolean;
  /** Reset to first cursor */
  resetPagination: VoidFunction;
  /** Flag indicating whether or not the list is active */
  isListActive: boolean;
  /** Flag indicating whether or not the mobile feature modal is active */
  featuresModalActive: boolean;
  /** Flag indicating whether or not the mobile search modal is active */
  mobileSearchModalActive: boolean;
  /** Flag indicating whether or not the search nav form is active */
  searchNavFormActive: boolean;
  /** Set the active features for the search */
  setActiveFilters: (features: Feature[]) => void;
  /** Set the map geo search box */
  setMapGeoSearchBox: (geoBox: Geometry) => void;
  /** The map geo search box */
  geoSearchBox: Geometry | undefined;
  /** Toggle the map active state */
  toggleIsListActive: VoidFunction;
  /** The initial map coordinates */
  centerPoint: Point | undefined;
  /** The initial map zoom level */
  mapZoom?: number;
  searchType: ListingsSearchType;
  checkInDate: moment.Moment | null;
  checkOutDate: moment.Moment | null;
  setCheckInDate: React.Dispatch<React.SetStateAction<moment.Moment | null>>;
  setCheckOutDate: React.Dispatch<React.SetStateAction<moment.Moment | null>>;
  placeId: string | null;
  placeName: string | null;
  adultCount: number;
  setAdultCount: React.Dispatch<React.SetStateAction<number>>;
  childCount: number;
  setChildCount: React.Dispatch<React.SetStateAction<number>>;
  infantCount: number;
  setInfantCount: React.Dispatch<React.SetStateAction<number>>;
  petCount: number;
  setPetCount: React.Dispatch<React.SetStateAction<number>>;
  minNumberOfRooms: number;
  setMinNumberOfRooms: React.Dispatch<React.SetStateAction<number>>;
  minNumberOfBathrooms: number;
  setMinNumberOfBathrooms: React.Dispatch<React.SetStateAction<number>>;
  minPrice: number | null;
  setMinPrice: React.Dispatch<React.SetStateAction<number | null>>;
  maxPrice: number | null;
  setMaxPrice: React.Dispatch<React.SetStateAction<number | null>>;
  guestCountHumanReadableValue: string;
  /** All of the property types currently applied to the search */
  propertyTypes: PropertyType[];
  /** Set the active property types for the search */
  setPropertyTypes: (propertyTypes: PropertyType[]) => void;
  /** All of the unique property types currently applied to the search */
  uniquePropertyTypes: UniquePropertyType[];
  /** Set the active unique property types for the search */
  setUniquePropertyTypes: (uniquePropertyTypes: UniquePropertyType[]) => void;
  nextPage: () => void;
  prevPage: () => void;
};

const SearchContext = React.createContext<SearchContext>(undefined!);

type SearchProviderProps = {
  accountId?: string;
  allFeatures: Feature[];
  children: React.ReactNode;
  initActiveFeatures?: string[];
  initPropertyTypes?: PropertyType[];
  initUniquePropertyTypes?: UniquePropertyType[];
  initMinNumberOfBathrooms?: number;
  initMinNumberOfRooms?: number;
  initMinPrice: number | null;
  initMaxPrice: number | null;
  initAdultCount?: number;
  initChildCount?: number;
  initInfantCount?: number;
  initPetCount?: number;
  initMapCoordinates?: Geometry;
  initMapZoom?: number;
  initSearchType: ListingsSearchType;
  initCheckInDate: moment.Moment | null;
  initCheckOutDate: moment.Moment | null;
  placeId?: string;
  placeName?: string;
  cursorAfter: string | null;
  cursorBefore: string | null;
  pageSize?: number;
};

// page size should be conducive to grid layouts with 2, 3, and 5 columns
export const searchPageSize = 30;

const calculateMapZoom = (placeType: string) => {
  if (placeType === 'country') {
    return 5;
  } else if (placeType === 'administrative_area_level_1') {
    return 7;
  } else if (placeType === 'level_one') {
    return 7;
  } else {
    return 11;
  }
};

export const SearchProvider: React.FC<SearchProviderProps> = ({
  accountId,
  allFeatures,
  children,
  cursorAfter: _cursorAfter,
  cursorBefore: _cursorBefore,
  pageSize: _pageSize,
  initActiveFeatures = [],
  initAdultCount = 1,
  initCheckInDate,
  initCheckOutDate,
  initChildCount = 0,
  initInfantCount = 0,
  initMapCoordinates,
  initMapZoom,
  initMinNumberOfBathrooms = 0,
  initMinNumberOfRooms = 0,
  initMinPrice,
  initMaxPrice,
  initPetCount = 0,
  initPropertyTypes = [],
  initSearchType,
  initUniquePropertyTypes = [],
  placeId,
  placeName,
}) => {
  const {
    activateMobileSearchModal,
    deactivateMobileSearchModal,
    mobileSearchModalActive,
  } = useMobileSearchModal();

  const {
    on: searchNavFormActive,
    setOn: activateSearchNavForm,
    setOff: deactivateSearchNavForm,
  } = useToggle(false);

  const {
    on: featuresModalActive,
    setOn: activateFeaturesModal,
    setOff: deactivateFeaturesModal,
  } = useToggle(false);

  const { on: isListActive, toggle: toggleIsListActive } = useToggle(true);

  const [cursors, setCursors] = React.useState<{
    cursorBefore: undefined | string;
    cursorAfter: undefined | string;
  }>({
    cursorAfter: _cursorAfter ? _cursorAfter : undefined,
    cursorBefore: _cursorBefore ? _cursorBefore : undefined,
  });

  const [pageSize] = React.useState<number>(_pageSize || searchPageSize);

  const resetPagination = React.useCallback(() => {
    setCursors({ cursorBefore: undefined, cursorAfter: undefined });
  }, []);

  const [searchType, setSearchType] =
    React.useState<ListingsSearchType>(initSearchType);
  const [centerPoint, setCenterPoint] = React.useState<Point | undefined>(
    initMapCoordinates?.centerPoint
  );
  const [geoSearchBox, _setMapGeoSearchBox] = React.useState<
    Geometry | undefined
  >(initMapCoordinates);
  const [mapZoom, setMapZoom] = React.useState(initMapZoom);
  const [minNumberOfRooms, setMinNumberOfRooms] =
    React.useState(initMinNumberOfRooms);
  const [minNumberOfBathrooms, setMinNumberOfBathrooms] = React.useState(
    initMinNumberOfBathrooms
  );
  const [minPrice, setMinPrice] = React.useState<number | null>(initMinPrice);
  const debouncedMinPrice = useDebounce(minPrice, 300);
  const [maxPrice, setMaxPrice] = React.useState<number | null>(initMaxPrice);
  const debouncedMaxPrice = useDebounce(maxPrice, 300);

  const [adultCount, setAdultCount] = React.useState(initAdultCount);
  const [childCount, setChildCount] = React.useState(initChildCount);
  const [infantCount, setInfantCount] = React.useState(initInfantCount);
  const [petCount, setPetCount] = React.useState(initPetCount);
  const [guestCountHumanReadableValue, setGuestCountHumanReadableValue] =
    React.useState('');
  const [checkInDate, setCheckInDate] = React.useState<moment.Moment | null>(
    initCheckInDate
  );
  const [checkOutDate, setCheckOutDate] = React.useState<moment.Moment | null>(
    initCheckOutDate
  );

  const [stayLength, setStayLength] = React.useState<number | null>(null);

  const { setListings, setSearch } = useAnalytics();

  React.useEffect(() => {
    var queryParams = new URLSearchParams(window.location.search);
    queryParams.set('adultCount', adultCount.toString());

    history.replaceState(null, '', '?' + queryParams.toString());
  }, [adultCount]);

  React.useEffect(() => {
    var queryParams = new URLSearchParams(window.location.search);
    queryParams.set('childCount', childCount.toString());

    history.replaceState(null, '', '?' + queryParams.toString());
  }, [childCount]);

  React.useEffect(() => {
    var queryParams = new URLSearchParams(window.location.search);
    queryParams.set('infantCount', infantCount.toString());

    history.replaceState(null, '', '?' + queryParams.toString());
  }, [infantCount]);

  React.useEffect(() => {
    var queryParams = new URLSearchParams(window.location.search);
    queryParams.set('petCount', petCount.toString());

    history.replaceState(null, '', '?' + queryParams.toString());
  }, [petCount]);

  React.useEffect(() => {
    var queryParams = new URLSearchParams(window.location.search);
    queryParams.set('minNumberOfRooms', minNumberOfRooms.toString());

    history.replaceState(null, '', '?' + queryParams.toString());
  }, [minNumberOfRooms]);

  React.useEffect(() => {
    var queryParams = new URLSearchParams(window.location.search);
    queryParams.set('minNumberOfBathrooms', minNumberOfBathrooms.toString());

    history.replaceState(null, '', '?' + queryParams.toString());
  }, [minNumberOfBathrooms]);

  React.useEffect(() => {
    var queryParams = new URLSearchParams(window.location.search);
    queryParams.set('minPrice', minPrice ? minPrice.toString() : '');

    history.replaceState(null, '', '?' + queryParams.toString());
  }, [minPrice]);

  React.useEffect(() => {
    var queryParams = new URLSearchParams(window.location.search);
    queryParams.set('maxPrice', maxPrice ? maxPrice.toString() : '');

    history.replaceState(null, '', '?' + queryParams.toString());
  }, [maxPrice]);

  React.useEffect(() => {
    var queryParams = new URLSearchParams(window.location.search);
    queryParams.set(
      'checkInDate',
      checkInDate ? checkInDate.format('YYYY-MM-DD') : ''
    );

    history.replaceState(null, '', '?' + queryParams.toString());
  }, [checkInDate]);

  React.useEffect(() => {
    var queryParams = new URLSearchParams(window.location.search);
    queryParams.set(
      'checkOutDate',
      checkOutDate ? checkOutDate.format('YYYY-MM-DD') : ''
    );

    history.replaceState(null, '', '?' + queryParams.toString());
  }, [checkOutDate]);

  React.useEffect(() => {
    var queryParams = new URLSearchParams(window.location.search);
    queryParams.set('pageSize', pageSize.toString());

    history.replaceState(null, '', '?' + queryParams.toString());
  }, [pageSize]);

  React.useEffect(() => {
    if (checkInDate && checkOutDate) {
      const nights = checkOutDate.diff(checkInDate, 'days');
      setStayLength(nights);
    } else {
      setStayLength(null);
    }
  }, [checkInDate, checkOutDate]);

  const setMapGeoSearchBox = React.useCallback((geometry: Geometry) => {
    var queryParams = new URLSearchParams(window.location.search);

    // Set new or modify existing parameter value.
    queryParams.set('searchType', ListingsSearchType.Map);
    queryParams.set('northEastLat', geometry.northEastPoint.lat.toString());
    queryParams.set('northEastLng', geometry.northEastPoint.lng.toString());
    queryParams.set('southWestLat', geometry.southWestPoint.lat.toString());
    queryParams.set('southWestLng', geometry.southWestPoint.lng.toString());
    queryParams.set('centerLat', geometry.centerPoint.lat.toString());
    queryParams.set('centerLng', geometry.centerPoint.lng.toString());

    history.replaceState(null, '', '?' + queryParams.toString());

    setSearchType(ListingsSearchType.Map);

    _setMapGeoSearchBox(geometry);
  }, []);

  // Update the guest count human readable value if the counts change
  React.useEffect(() => {
    const description = guestDescription(
      adultCount,
      childCount,
      infantCount,
      petCount
    );

    setGuestCountHumanReadableValue(description);
  }, [adultCount, childCount, infantCount, petCount]);

  const [activeFilters, _setActiveFilters] = React.useState<Feature[]>(
    allFeatures.filter((feature) => initActiveFeatures.includes(feature.key))
  );

  const setActiveFilters = React.useCallback((features: Feature[]) => {
    var queryParams = new URLSearchParams(window.location.search);

    // Set new or modify existing parameter value.
    queryParams.set('activeFeatures', features.map((f) => f.key).join(','));

    history.replaceState(null, '', '?' + queryParams.toString());

    _setActiveFilters(features);
  }, []);

  const [propertyTypes, _setPropertyTypes] =
    React.useState<PropertyType[]>(initPropertyTypes);

  const setPropertyTypes = React.useCallback(
    (propertyTypes: PropertyType[]) => {
      var queryParams = new URLSearchParams(window.location.search);

      // Set new or modify existing parameter value.

      queryParams.set('propertyTypes', propertyTypes.map((p) => p).join(','));

      history.replaceState(null, '', '?' + queryParams.toString());

      _setPropertyTypes(propertyTypes);
    },
    []
  );

  const [uniquePropertyTypes, _setUniquePropertyTypes] = React.useState<
    UniquePropertyType[]
  >(initUniquePropertyTypes);

  const setUniquePropertyTypes = React.useCallback(
    (uniquePropertyTypes: UniquePropertyType[]) => {
      var queryParams = new URLSearchParams(window.location.search);

      // Set new or modify existing parameter value.

      queryParams.set(
        'uniquePropertyTypes',
        uniquePropertyTypes.map((p) => p).join(',')
      );

      history.replaceState(null, '', '?' + queryParams.toString());

      _setUniquePropertyTypes(uniquePropertyTypes);
    },
    []
  );

  const [minPriceCents, setMinPriceCents] = React.useState<number | undefined>(
    undefined
  );
  const [maxPriceCents, setMaxPriceCents] = React.useState<number | undefined>(
    undefined
  );

  React.useEffect(() => {
    const minPriceCents =
      stayLength && debouncedMinPrice
        ? stayLength * Math.round(debouncedMinPrice * 100)
        : undefined;
    const maxPriceCents =
      stayLength && debouncedMaxPrice
        ? stayLength * Math.round(debouncedMaxPrice * 100)
        : undefined;

    setMinPriceCents(minPriceCents);
    setMaxPriceCents(maxPriceCents);
  }, [debouncedMinPrice, debouncedMaxPrice, stayLength]);

  /**
   * NOTE - If the variables provided below differ from the variables
   * provided to the imperative query executed from within a page components
   * `getServerSideProps`, this will result in a cache miss
   * and another client side request will be made to hydrate the UI.
   */
  const { data: listingsQueryResults, loading: listingsLoading } =
    useListingsSearchQuery({
      ssr: isSSR,
      notifyOnNetworkStatusChange: true,
      variables: {
        accountId,
        minNumberOfRooms: minNumberOfRooms,
        minNumberOfBathrooms: minNumberOfBathrooms,
        minGuestCapacity: adultCount + childCount,
        minPriceCents: minPriceCents,
        maxPriceCents: maxPriceCents,
        propertyTypes: propertyTypes.length > 0 ? propertyTypes : undefined,
        uniquePropertyTypes:
          uniquePropertyTypes.length > 0 ? uniquePropertyTypes : undefined,
        features: activeFilters.map((f) => f.key),
        checkInDate: checkInDate
          ? formatDateYYYYMMDD(checkInDate.toDate())
          : undefined,
        checkOutDate: checkOutDate
          ? formatDateYYYYMMDD(checkOutDate.toDate())
          : undefined,
        cursorAfter: cursors.cursorAfter,
        cursorBefore: cursors.cursorBefore,
        pageSize: pageSize,
        placeId: placeId,
        searchType: accountId ? ListingsSearchType.Map : searchType,
        northEastLat: accountId
          ? 90
          : searchType === ListingsSearchType.Region
            ? undefined
            : geoSearchBox?.northEastPoint.lat,
        northEastLng: accountId
          ? 180
          : searchType === ListingsSearchType.Region
            ? undefined
            : geoSearchBox?.northEastPoint.lng,
        southWestLat: accountId
          ? -90
          : searchType === ListingsSearchType.Region
            ? undefined
            : geoSearchBox?.southWestPoint.lat,
        southWestLng: accountId
          ? -180
          : searchType === ListingsSearchType.Region
            ? undefined
            : geoSearchBox?.southWestPoint.lng,
        centerLat: accountId
          ? 0
          : searchType === ListingsSearchType.Region
            ? undefined
            : geoSearchBox?.centerPoint.lat,
        centerLng: accountId
          ? 0
          : searchType === ListingsSearchType.Region
            ? undefined
            : geoSearchBox?.centerPoint.lng,
        petCount: petCount,
      },
    });

  const nextPage = React.useCallback(() => {
    if (
      listingsQueryResults?.listingsSearch.__typename ===
      'ListingSearchCursorCollection'
    ) {
      var queryParams = new URLSearchParams(window.location.search);
      queryParams.delete('cursorBefore');

      if (listingsQueryResults.listingsSearch.metadata.cursorAfter) {
        queryParams.set(
          'cursorAfter',
          listingsQueryResults.listingsSearch.metadata.cursorAfter
        );

        setCursors({
          cursorAfter: listingsQueryResults.listingsSearch.metadata.cursorAfter,
          cursorBefore: undefined,
        });
      }

      history.replaceState(null, '', '?' + queryParams.toString());
    }
  }, [listingsQueryResults]);

  const prevPage = React.useCallback(() => {
    if (
      listingsQueryResults?.listingsSearch.__typename ===
      'ListingSearchCursorCollection'
    ) {
      var queryParams = new URLSearchParams(window.location.search);
      queryParams.delete('cursorAfter');

      if (listingsQueryResults.listingsSearch.metadata.cursorBefore) {
        queryParams.set(
          'cursorBefore',
          listingsQueryResults.listingsSearch.metadata.cursorBefore
        );

        setCursors({
          cursorAfter: undefined,
          cursorBefore:
            listingsQueryResults.listingsSearch.metadata.cursorBefore,
        });
      }

      history.replaceState(null, '', '?' + queryParams.toString());
    }
  }, [listingsQueryResults]);

  // if (isSSR && listingsQueryResults === undefined && isDev) {
  //   throw new Error('SSR is broken');
  // }

  React.useEffect(() => {
    if (
      listingsQueryResults?.listingsSearch.__typename ===
      'ListingSearchCursorCollection'
    ) {
      const search = {
        id: uuid(),
        type: searchType,
        destination: placeName,
        guestCount: adultCount + childCount,
        checkInDate: checkInDate ? formatDateYYYYMMDD(checkInDate) : undefined,
        checkOutDate: checkOutDate
          ? formatDateYYYYMMDD(checkOutDate)
          : undefined,
      };

      setSearch(search);
    } else {
      setSearch(null);
    }
  }, [listingsQueryResults]);

  React.useEffect(() => {
    if (
      centerPoint === undefined &&
      listingsQueryResults?.listingsSearch?.__typename ===
        'ListingSearchCursorCollection' &&
      listingsQueryResults.listingsSearch.searchPlace?.geometry?.centerPoint
    ) {
      setCenterPoint(
        listingsQueryResults.listingsSearch.searchPlace?.geometry?.centerPoint
      );
      setMapZoom(
        calculateMapZoom(
          listingsQueryResults.listingsSearch.searchPlace?.placeType || ''
        )
      );
    }
  }, [listingsQueryResults, centerPoint]);

  React.useEffect(() => {
    if (
      listingsQueryResults?.listingsSearch?.__typename ===
      'ListingSearchCursorCollection'
    ) {
      setListings(listingsQueryResults.listingsSearch.entries);
    } else {
      setListings([]);
    }
  }, [listingsQueryResults]);

  const contextValue: SearchContext = {
    activateFeaturesModal,
    activateMobileSearchModal,
    activateSearchNavForm,
    activeFilters,
    adultCount,
    allFeatures,
    centerPoint,
    checkInDate,
    checkOutDate,
    childCount,
    deactivateFeaturesModal,
    deactivateMobileSearchModal,
    deactivateSearchNavForm,
    featuresModalActive,
    guestCountHumanReadableValue,
    infantCount,
    isListActive,
    listingsLoading,
    listingsQueryResults,
    mapZoom,
    minNumberOfBathrooms,
    minNumberOfRooms,
    minPrice,
    maxPrice,
    mobileSearchModalActive,
    petCount,
    placeId: placeId || null,
    placeName: placeName || null,
    propertyTypes,
    resetPagination,
    searchNavFormActive,
    searchType,
    setActiveFilters,
    setAdultCount,
    setCheckInDate,
    setCheckOutDate,
    setChildCount,
    setInfantCount,
    setMapGeoSearchBox,
    setMinNumberOfBathrooms,
    setMinNumberOfRooms,
    setMinPrice,
    setMaxPrice,
    setPetCount,
    setPropertyTypes,
    setUniquePropertyTypes,
    toggleIsListActive,
    uniquePropertyTypes,
    nextPage,
    prevPage,
    geoSearchBox,
  };

  return (
    <SearchContext.Provider value={contextValue}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = React.useContext(SearchContext);

  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }

  return context;
};

export async function getSearchServerSideProps(
  context: GetServerSidePropsContext
) {
  const accountSlug = context.query?.slug as string | undefined;
  let account:
    | null
    | Awaited<
        ReturnType<typeof imperativeNodeAccountBySlug>
      >['data']['accountBySlug'] = null;

  // united states place id
  const placeId =
    (context.query?.placeId as string) || 'ChIJCzYy5IS16lQRQrfeQ5K5Oxw';

  let searchType = context.query?.searchType
    ? (context.query?.searchType as ListingsSearchType)
    : ListingsSearchType.Region;

  const cursorAfter = context.query?.cursorAfter as string | undefined;
  const cursorBefore = context.query?.cursorBefore as string | undefined;

  const pageSize = context.query?.pageSize
    ? parseInt(context.query?.pageSize as string)
    : searchPageSize;

  const initActiveFeatures = context.query?.activeFeatures
    ? (context.query?.activeFeatures as string)
        .split(',')
        .filter((f) => f.length > 0)
    : [];

  const initPropertyTypes = context.query?.propertyTypes
    ? ((context.query?.propertyTypes as string).split(',') as PropertyType[])
    : [];

  const initUniquePropertyTypes = context.query?.uniquePropertyTypes
    ? ((context.query?.uniquePropertyTypes as string).split(
        ','
      ) as UniquePropertyType[])
    : [];

  const checkInDate = context.query?.checkInDate
    ? context.query?.checkInDate
    : null;

  const checkOutDate = context.query?.checkOutDate
    ? context.query?.checkOutDate
    : null;

  const initMinNumberOfRooms = context.query?.minNumberOfRooms
    ? parseInt(context.query?.minNumberOfRooms as string)
    : 0;

  const initMinNumberOfBathrooms = context.query?.minNumberOfBathrooms
    ? parseInt(context.query?.minNumberOfBathrooms as string)
    : 0;

  const minPrice = context.query?.minPrice
    ? parseFloat(context.query?.minPrice as string)
    : null;

  const maxPrice = context.query?.maxPrice
    ? parseFloat(context.query?.maxPrice as string)
    : null;

  const initAdultCount = context.query?.adultCount
    ? parseInt(context.query?.adultCount as string)
    : 1;

  const initChildCount = context.query?.childCount
    ? parseInt(context.query?.childCount as string)
    : 0;

  const initInfantCount = context.query?.infantCount
    ? parseInt(context.query?.infantCount as string)
    : 0;

  const initPetCount = context.query?.petCount
    ? parseInt(context.query?.petCount as string)
    : 0;

  const zoom = context.query?.zoom
    ? parseInt(context.query?.zoom as string, 10)
    : null;

  let northEastLat = context.query?.northEastLat
    ? parseFloat(context.query?.northEastLat as string)
    : undefined;

  let northEastLng = context.query?.northEastLng
    ? parseFloat(context.query?.northEastLng as string)
    : undefined;

  let southWestLat = context.query?.southWestLat
    ? parseFloat(context.query?.southWestLat as string)
    : undefined;

  let southWestLng = context.query?.southWestLng
    ? parseFloat(context.query?.southWestLng as string)
    : undefined;

  let centerLat = context.query?.centerLat
    ? parseFloat(context.query?.centerLat as string)
    : undefined;

  let centerLng = context.query?.centerLng
    ? parseFloat(context.query?.centerLng as string)
    : undefined;

  if (accountSlug) {
    const account_response = await imperativeNodeAccountBySlug(accountSlug);
    account = account_response.data.accountBySlug;
    if (account.__typename == 'NotFoundError')
      return {
        notFound: true,
      };
  }

  const apolloClient = createApolloClient();

  return await Promise.all([
    await auth(context),
    await imperativeNodeFeaturesListAllForSearchFiltersQuery(),
    /**
     * NOTE - If the variables provided below differ from the variables
     * provided to the hook executed from within the `SearchProvider`,
     * this will result in a cache miss and another client side
     * request will be made to hydrate the UI.
     */
    await apolloClient.query<ListingsSearchQuery>({
      query: ListingsSearchDocument,
      variables: {
        accountId: account?.id,
        minNumberOfRooms: initMinNumberOfRooms,
        minNumberOfBathrooms: initMinNumberOfBathrooms,
        minGuestCapacity: initAdultCount + initChildCount,
        propertyTypes:
          initPropertyTypes.length > 0 ? initPropertyTypes : undefined,
        uniquePropertyTypes:
          initUniquePropertyTypes.length > 0
            ? initUniquePropertyTypes
            : undefined,
        features: initActiveFeatures.map((f) => f),
        checkInDate: checkInDate ? checkInDate : undefined,
        checkOutDate: checkOutDate ? checkOutDate : undefined,
        pageSize: pageSize,
        cursorAfter,
        cursorBefore,
        placeId: placeId,
        searchType: searchType,
        northEastLat: northEastLat,
        northEastLng: northEastLng,
        southWestLat: southWestLat,
        southWestLng: southWestLng,
        centerLat: centerLat,
        centerLng: centerLng,
        petCount: initPetCount,
      },
    }),
  ]).then(
    ([
      session,
      {
        data: { featuresListAllForSearchFilters },
      },
      listingsSearchResults,
    ]) => {
      const initMapCoordinatesParsedInParams =
        centerLat !== undefined &&
        centerLng !== undefined &&
        southWestLat !== undefined &&
        southWestLng !== undefined &&
        northEastLat !== undefined &&
        northEastLng !== undefined;
      if (
        listingsSearchResults.data.listingsSearch.__typename ===
          'ListingSearchCursorCollection' &&
        listingsSearchResults.data.listingsSearch.searchPlace?.geometry &&
        listingsSearchResults.data.listingsSearch.searchPlace?.geometry
          .centerPoint &&
        listingsSearchResults.data.listingsSearch.searchPlace?.geometry
          .southWestPoint &&
        listingsSearchResults.data.listingsSearch.searchPlace?.geometry
          .northEastPoint
      ) {
        const props = {
          account,
          initActiveFeatures,
          features: featuresListAllForSearchFilters,
          cursorAfter: cursorAfter ? cursorAfter : null,
          cursorBefore: cursorBefore ? cursorBefore : null,
          pageSize,
          placeId,
          placeName:
            listingsSearchResults.data.listingsSearch.searchPlace?.name,
          session,
          initSearchType: searchType,
          initMapZoom:
            zoom ||
            calculateMapZoom(
              listingsSearchResults.data.listingsSearch.searchPlace
                ?.placeType || ''
            ),
          initMapCoordinates: {
            centerPoint: initMapCoordinatesParsedInParams
              ? { lat: centerLat!, lng: centerLng! }
              : listingsSearchResults.data.listingsSearch.searchPlace?.geometry
                  .centerPoint,
            southWestPoint: initMapCoordinatesParsedInParams
              ? { lat: southWestLat!, lng: southWestLng! }
              : listingsSearchResults.data.listingsSearch.searchPlace?.geometry
                  .southWestPoint,
            northEastPoint: initMapCoordinatesParsedInParams
              ? { lat: northEastLat!, lng: northEastLng! }
              : listingsSearchResults.data.listingsSearch.searchPlace?.geometry
                  .northEastPoint,
          },
          initPropertyTypes,
          initUniquePropertyTypes,
          initMinNumberOfBathrooms,
          initMinNumberOfRooms,
          initMinPrice: minPrice,
          initMaxPrice: maxPrice,
          initAdultCount,
          initChildCount,
          initInfantCount,
          initPetCount,
          initCheckInDate: checkInDate,
          initCheckOutDate: checkOutDate,
        };

        // @ts-expect-error
        props[APOLLO_STATE_PROP_NAME] = apolloClient.cache.extract();

        return {
          props,
        };
      } else {
        Sentry.captureException(
          new Error('Unexpected search results, geometry is likely missing')
        );
        return {
          redirect: {
            destination: `${routes.search}`,
            permanent: false,
          },
        };
      }
    }
  );
}
